/**
 * Branch change captions — turns a screen-design diff (RFC-6902-style patch ops + the design
 * snapshot) into PLAIN-LANGUAGE captions for the branching diff/conflict UI, e.g.
 * "Label 'Title': text changed", "Button 'Save' added".
 *
 * This is the navigation/selection layer agreed for cluster 3: the user reads captions (never raw
 * field names or JSON-pointer paths), clicks one, and the detail pane shows the matching visual /
 * code diff and the inline Use Source / Use Target action operates on that change.
 *
 * Pure and framework-free so it is unit-testable in isolation; the UI passes an already-normalized
 * snapshot object (callers own snapshot unwrapping).
 */

/** A single RFC-6902-ish patch operation as produced by the branching diff engine. */
export interface DiffPatchOp {
  op: string;
  path: string;
  value?: unknown;
  previous?: unknown;
}

export type BranchChangeKind = "added" | "removed" | "modified";

export interface BranchChangeCaption {
  /** Stable key for React lists / selection. */
  id: string;
  /** Plain-language description shown to the user. */
  caption: string;
  kind: BranchChangeKind;
  /** Display-item id of the changed component, for canvas highlight (null when unresolved). */
  componentId: string | null;
  componentLabel: string | null;
  /** Human label of the changed property (null for whole-component add/remove). */
  propertyLabel: string | null;
  /** Formatted scalar values when the change is a leaf property edit. */
  before: string | null;
  after: string | null;
  /** Raw JSON-pointer path, kept for the opt-in "technical details" view. */
  path: string;
}

/** A component property entry: { name, symbol: { fixedValue }, selectedType }. */
interface ComponentProperty {
  name?: string;
  symbol?: { fixedValue?: unknown };
}

/** The component descriptor on a display-item node — `name` is the component TYPE (Button, Label…). */
interface DesignNodeItem {
  name?: string;
  properties?: ComponentProperty[];
}

/** Minimal shape of a design tree node we traverse to resolve names. */
interface DesignNode {
  id?: string | number;
  Id?: string | number;
  kuikaId?: string | number;
  KuikaId?: string | number;
  name?: string;
  Name?: string;
  friendlyName?: string;
  FriendlyName?: string;
  title?: string;
  Title?: string;
  componentName?: string;
  ComponentName?: string;
  type?: string;
  Type?: string;
  item?: DesignNodeItem;
  displayItems?: DesignNode[];
  children?: DesignNode[];
  items?: DesignNode[];
  elements?: DesignNode[];
}

// Property names whose fixed value is shown as a component's "content" in a caption.
const CONTENT_PROP_NAMES: readonly string[] = ["text", "title", "label", "value", "headertext", "placeholder", "name"];

// Path tokens that are structural plumbing rather than user-meaningful property names.
const STRUCTURAL_TOKENS: ReadonlySet<string> = new Set([
  "json",
  "displayitems",
  "children",
  "items",
  "elements",
  "item",
  "properties",
  "symbol",
  "selectedtype"
]);

// Friendly labels for common leaf property names; anything else is humanized from camelCase.
const PROPERTY_LABELS: ReadonlyMap<string, string> = new Map([
  ["text", "text"],
  ["title", "title"],
  ["value", "value"],
  ["tooltip", "tooltip"],
  ["placeholder", "placeholder"],
  ["visible", "visibility"],
  ["disabled", "disabled state"],
  ["typoid", "typography"],
  ["fillid", "fill"],
  ["borderid", "border"],
  ["shadowid", "shadow"],
  ["positionid", "position"],
  ["color", "color"],
  ["backgroundcolor", "background color"],
  ["width", "width"],
  ["height", "height"],
  ["iconlibrary", "icon library"],
  ["iconname", "icon"],
  ["datasource", "data source"]
]);

function isDesignNode(value: unknown): value is DesignNode {
  return typeof value === "object" && value !== null;
}

function nodeLabel(node: DesignNode | null): string | null {
  if (!node) return null;
  const candidates: (string | undefined)[] = [
    node.name,
    node.Name,
    node.friendlyName,
    node.FriendlyName,
    node.title,
    node.Title,
    node.componentName,
    node.ComponentName,
    node.type,
    node.Type
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
  }
  return null;
}

// The component TYPE (Button, Label, Table…) from the node's `item.name`.
function componentType(node: DesignNode): string | null {
  const raw: string | undefined = node.item?.name;
  return typeof raw === "string" && raw.trim() ? raw.trim() : null;
}

// A short, human "content" for the component — the first non-empty text-ish property fixed value.
function componentContent(node: DesignNode): string | null {
  const props: ComponentProperty[] | undefined = node.item?.properties;
  if (!Array.isArray(props)) return null;
  for (const wanted of CONTENT_PROP_NAMES) {
    const prop: ComponentProperty | undefined = props.find((p) => (p.name ?? "").toLowerCase() === wanted);
    const fixed: unknown = prop?.symbol?.fixedValue;
    if (typeof fixed === "string" && fixed.trim()) {
      const trimmed: string = fixed.trim();
      return trimmed.length > 30 ? `${trimmed.slice(0, 29)}…` : trimmed;
    }
  }
  return null;
}

// The display item's own name/label (the instance identifier shown in the layers tree, e.g. "Btn01").
// Distinct from the component TYPE; excludes the type/componentName fallbacks nodeLabel() allows.
function instanceName(node: DesignNode | null): string | null {
  if (!node) return null;
  const candidates: (string | undefined)[] = [
    node.name,
    node.Name,
    node.friendlyName,
    node.FriendlyName,
    node.title,
    node.Title
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
  }
  return null;
}

// Preferred caption label: `Type "Instance"` → `Type` → instance/content → nodeLabel (last resort).
function componentDisplayLabel(node: DesignNode | null): string | null {
  if (!node) return null;
  const type: string | null = componentType(node);
  const identifier: string | null = instanceName(node) ?? componentContent(node);
  if (type && identifier) return `${type} "${identifier}"`;
  if (type) return type;
  if (identifier) return identifier;
  return nodeLabel(node);
}

function nodeIdValue(node: DesignNode | null): string | null {
  if (!node) return null;
  const raw: string | number | undefined = node.id ?? node.Id ?? node.kuikaId ?? node.KuikaId;
  return raw == null ? null : String(raw);
}

function findChildById(container: DesignNode | null, idValue: string): DesignNode | null {
  if (!container) return null;
  const lists: (DesignNode[] | undefined)[] = [
    container.displayItems,
    container.children,
    container.items,
    container.elements
  ];
  for (const list of lists) {
    if (Array.isArray(list)) {
      const found: DesignNode | undefined = list.find((item) => nodeIdValue(item) === idValue);
      if (found) return found;
    }
  }
  return null;
}

function splitPath(path: string): string[] {
  return path
    .split("/")
    .map((token) => token.trim().replace(/~1/g, "/").replace(/~0/g, "~"))
    .filter(Boolean);
}

function humanizeToken(token: string): string {
  const spaced: string = token
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return spaced.toLowerCase();
}

function propertyLabelFromTail(tailTokens: string[]): string | null {
  const meaningful: string[] = tailTokens.filter((token) => {
    if (/^\[id=.+\]$/i.test(token)) return false;
    if (/^\d+$/.test(token)) return false;
    return !STRUCTURAL_TOKENS.has(token.toLowerCase());
  });
  if (meaningful.length === 0) return null;
  const leaf: string = meaningful[meaningful.length - 1];
  const mapped: string | undefined = PROPERTY_LABELS.get(leaf.toLowerCase());
  return mapped ?? humanizeToken(leaf);
}

function formatScalar(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed: string = value.trim();
    if (trimmed === "") return null;
    return trimmed.length > 60 ? `${trimmed.slice(0, 59)}…` : trimmed;
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null; // objects/arrays are not scalar leaf values
}

const GUID_TOKEN: RegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Value shown inline in a caption ("from X to Y"). Omits GUID-ish or long values that read as noise
// (e.g. style-id references) so the sentence stays meaningful.
function captionValueText(value: unknown): string | null {
  const scalar: string | null = formatScalar(value);
  if (scalar == null || scalar.length > 40) return null;
  if (scalar.split("|").every((token) => GUID_TOKEN.test(token))) return null;
  return scalar;
}

const ID_TOKEN: RegExp = /^\[id=(.+)\]$/i;

interface ResolvedComponent {
  componentId: string | null;
  componentLabel: string | null;
  tailTokens: string[];
}

// Walks the path's [id=...] segments against the snapshot, returning the deepest resolved
// component plus the remaining (property) tokens after it.
function resolveComponent(snapshot: DesignNode, tokens: string[]): ResolvedComponent {
  let cursor: DesignNode | null = snapshot;
  let componentId: string | null = null;
  let componentLabel: string | null = null;
  let lastIdTokenIndex = -1;

  for (let i = 0; i < tokens.length; i++) {
    const match: RegExpMatchArray | null = tokens[i].match(ID_TOKEN);
    if (!match) continue;
    const idValue: string = match[1].replace(/^"|"$/g, "").trim();
    if (idValue === "-1" || idValue === "") continue;
    const found: DesignNode | null = findChildById(cursor, idValue);
    componentId = idValue;
    componentLabel = componentDisplayLabel(found);
    cursor = found;
    lastIdTokenIndex = i;
  }

  const tailTokens: string[] = lastIdTokenIndex >= 0 ? tokens.slice(lastIdTokenIndex + 1) : tokens;
  return { componentId, componentLabel, tailTokens };
}

function captionKind(op: string): BranchChangeKind {
  const lowered: string = op.toLowerCase();
  if (lowered === "add") return "added";
  if (lowered === "remove") return "removed";
  return "modified";
}

function pluralize(word: string, count: number): string {
  if (count === 1 || word.endsWith("s")) return word;
  return `${word}s`;
}

// Counts the component types contained within a subtree (recursively, excluding the root itself),
// keyed by component type, so a container add/remove can say what it carried.
function collectDescendantTypes(root: DesignNode): Map<string, number> {
  const counts: Map<string, number> = new Map();
  const visit = (node: DesignNode): void => {
    const childLists: (DesignNode[] | undefined)[] = [node.children, node.displayItems, node.items, node.elements];
    for (const list of childLists) {
      if (!Array.isArray(list)) continue;
      for (const child of list) {
        const type: string | null = componentType(child) ?? instanceName(child);
        if (type) counts.set(type, (counts.get(type) ?? 0) + 1);
        visit(child);
      }
    }
  };
  visit(root);
  return counts;
}

// Short content summary for an added/removed container, e.g. "2 Buttons, 1 Label". Null when empty.
function summarizeSubtree(node: DesignNode | null): string | null {
  if (!node) return null;
  const counts: Map<string, number> = collectDescendantTypes(node);
  if (counts.size === 0) return null;
  const sorted: [string, number][] = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  const top: string[] = sorted.slice(0, 3).map(([type, count]) => `${count} ${pluralize(type, count)}`);
  return sorted.length > 3 ? `${top.join(", ")}…` : top.join(", ");
}

function composeCaption(
  label: string,
  propertyLabel: string | null,
  kind: BranchChangeKind,
  hasPrevious: boolean,
  beforeText: string | null,
  afterText: string | null,
  contentSummary: string | null
): string {
  // Whole-component change (no specific property). Append a content summary for containers.
  if (!propertyLabel) {
    const suffix: string = contentSummary ? ` — ${contentSummary}` : "";
    if (kind === "added") return `${label} added${suffix}`;
    if (kind === "removed") return `${label} removed${suffix}`;
    return `${label} changed`;
  }
  if (kind === "removed") return `${label} ${propertyLabel} cleared`;
  // No prior value (an add, or a previously-empty property) → "set [to X]".
  if (kind === "added" || !hasPrevious) {
    return afterText !== null ? `${label} ${propertyLabel} set to "${afterText}"` : `${label} ${propertyLabel} set`;
  }
  // Modified, with a prior value that existed. Show the transition when both ends are readable;
  // when a value is omitted as noise (e.g. a GUID style-id), fall back to a plain "changed".
  if (beforeText !== null && afterText !== null)
    return `${label} ${propertyLabel} changed from "${beforeText}" to "${afterText}"`;
  if (afterText !== null) return `${label} ${propertyLabel} changed to "${afterText}"`;
  return `${label} ${propertyLabel} changed`;
}

/**
 * Generates plain-language captions for a screen-design diff. `snapshot` is the (normalized)
 * design whose components provide names; when a component was added it may be absent from the
 * snapshot, so the added value's own label is used as a fallback.
 */
export function generateScreenDesignCaptions(snapshot: DesignNode, ops: DiffPatchOp[]): BranchChangeCaption[] {
  if (!isDesignNode(snapshot) || !Array.isArray(ops)) return [];
  return ops.map((op, index) => buildCaption(snapshot, op, index));
}

export interface ScreenConflictDescription {
  /** Display-item id of the conflicting component (for canvas highlight); null if unresolved. */
  componentId: string | null;
  /** Resolved component label, or null when the component couldn't be found in the snapshot. */
  componentLabel: string | null;
  propertyLabel: string | null;
  /** Best-effort plain-language label, e.g. `Button "SaveButton" — text`. */
  label: string;
}

/**
 * Describes a single merge-conflict path in plain language (no JSON pointer) for the conflict
 * navigation/header. Unlike a staged change, a conflict has no before→after transition — it just
 * identifies the component + property in question, which the Source/Target panes then compare.
 */
export function describeScreenConflict(snapshot: unknown, path: string): ScreenConflictDescription {
  if (!isDesignNode(snapshot) || typeof path !== "string" || path.trim() === "") {
    return { componentId: null, componentLabel: null, propertyLabel: null, label: "this change" };
  }
  const tokens: string[] = splitPath(path);
  const { componentId, componentLabel, tailTokens } = resolveComponent(snapshot, tokens);
  const propertyLabel: string | null = propertyLabelFromTail(tailTokens);
  const component: string = componentLabel ?? "Component";
  const label: string = propertyLabel ? `${component} — ${propertyLabel}` : component;
  return { componentId, componentLabel, propertyLabel, label };
}

// Builds one caption from a single patch op. When the component isn't in the snapshot (e.g. it was
// just added), the label carried by the patch value/previous is used, then a generic noun.
function buildCaption(snapshot: DesignNode, op: DiffPatchOp, index: number): BranchChangeCaption {
  const tokens: string[] = splitPath(op.path);
  const { componentId, componentLabel, tailTokens } = resolveComponent(snapshot, tokens);
  const kind: BranchChangeKind = captionKind(op.op);
  const propertyLabel: string | null = propertyLabelFromTail(tailTokens);

  const valueNode: DesignNode | null = isDesignNode(op.value) ? op.value : null;
  const prevNode: DesignNode | null = isDesignNode(op.previous) ? op.previous : null;
  const resolvedLabel: string =
    componentLabel ?? componentDisplayLabel(valueNode) ?? componentDisplayLabel(prevNode) ?? "Component";

  // For a whole-component add/remove, summarize what the container held (e.g. "2 Buttons, 1 Label").
  const contentSummary: string | null =
    propertyLabel === null ? summarizeSubtree(kind === "removed" ? prevNode : valueNode) : null;

  return {
    id: `${op.path}#${index}`,
    caption: composeCaption(
      resolvedLabel,
      propertyLabel,
      kind,
      formatScalar(op.previous) !== null,
      captionValueText(op.previous),
      captionValueText(op.value),
      contentSummary
    ),
    kind,
    componentId,
    componentLabel: resolvedLabel,
    propertyLabel,
    before: formatScalar(op.previous),
    after: formatScalar(op.value),
    path: op.path
  };
}
